import { useEffect, useRef, useState } from 'react';
import { LOCALE_META, SUPPORTED_LOCALES, normalizeLocale, type Locale } from '@wikigold/i18n';
import { INTRO_CONTENT, OFFICIAL_PRIVACY_URL, OFFICIAL_SUPPORT_EMAIL } from './content';
import './intro.css';

export interface IntroPageProps {
  locale: Locale;
  onLocaleChange: (locale: Locale) => void;
  onEnter: () => void;
  onRules: () => void;
}

type InfoKind = 'privacy' | 'contact';

function Brand() {
  return <span className="intro-brand" dir="ltr"><svg viewBox="49 21 66 67" aria-hidden="true" focusable="false"><image href="/assets/intro-reference-v2.jpg" width="1087" height="1447" /></svg><span>WikiGold</span></span>;
}

function FactIcon({ type }: { type: 'calendar' | 'timer' | 'trophy' }) {
  return <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {type === 'calendar' && <><rect x="7" y="10" width="34" height="32" rx="5" /><path d="M7 20h34M15 5v11M33 5v11" /><path strokeWidth="4" d="M16 28h.1M25 28h.1M33 28h.1M16 35h.1M25 35h.1M33 35h.1" /></>}
    {type === 'timer' && <><circle cx="24" cy="27" r="16" /><path d="M24 4v7M19 4h10M37 12l3 3M24 18v10" /></>}
    {type === 'trophy' && <><path d="M13 7h22v11c0 9-5 15-11 15s-11-6-11-15V7ZM13 11H5v5c0 6 4 10 11 10M35 11h8v5c0 6-4 10-11 10M24 33v8M16 43h16" /><path fill="currentColor" stroke="none" d="m24 12 2.2 4.5 5 .7-3.6 3.5.8 5-4.4-2.4-4.4 2.4.8-5-3.6-3.5 5-.7Z" /></>}
  </svg>;
}

export function IntroInfoDialog({ kind, locale, onClose }: { kind: InfoKind; locale: Locale; onClose: () => void }) {
  const copy = INTRO_CONTENT[locale];
  const dialog = useRef<HTMLDialogElement>(null);
  const paragraphs = kind === 'privacy' ? copy.privacyBody : copy.contactBody;
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialog.current?.showModal();
    return () => { if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus(); };
  }, []);
  return <dialog
    ref={dialog}
    className="intro-info-dialog"
    lang={locale}
    dir={LOCALE_META[locale].direction}
    aria-labelledby="intro-info-title"
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => { if (event.target === event.currentTarget) onClose(); }}
  >
    <div className="intro-info-content">
      <div className="intro-info-heading"><h2 id="intro-info-title">{copy[kind]}</h2><button className="intro-close" onClick={onClose} aria-label={copy.close}>×</button></div>
      {paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
      {kind === 'privacy'
        ? <a className="intro-info-link" href={OFFICIAL_PRIVACY_URL}>{copy.privacyPolicy}<span aria-hidden="true"> ↗</span></a>
        : <a className="intro-info-link intro-email" dir="ltr" href={`mailto:${OFFICIAL_SUPPORT_EMAIL}`}>{OFFICIAL_SUPPORT_EMAIL}</a>}
      <button className="intro-dialog-done" onClick={onClose}>{copy.close}</button>
    </div>
  </dialog>;
}

export function IntroPage({ locale, onLocaleChange, onEnter, onRules }: IntroPageProps) {
  const copy = INTRO_CONTENT[locale];
  const [info, setInfo] = useState<InfoKind | null>(null);
  const facts = [
    { icon: 'calendar', text: copy.days },
    { icon: 'timer', text: copy.duration },
    { icon: 'trophy', text: copy.leaderboard },
  ] as const;
  return <div className="intro-page" lang={locale} dir={LOCALE_META[locale].direction}>
    <div className="intro-container">
      <header className="intro-header">
        <Brand />
        <label className="intro-language">
          <span className="intro-sr-only">{copy.language}</span>
          <select aria-label={copy.language} value={locale} onChange={event => onLocaleChange(normalizeLocale(event.target.value))}>
            {SUPPORTED_LOCALES.map(language => <option key={language} value={language}>{LOCALE_META[language].nativeName}</option>)}
          </select>
          <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 7 5 5 5-5" /></svg>
        </label>
      </header>
      <main className="intro-main">
        <div className="intro-badge">{copy.badge}</div>
        <img className="intro-art" src="/assets/intro-mining-v2.png" alt="" width="1482" height="1062" />
        <h1 className="intro-title"><span>{copy.title[0]}</span><span>{copy.title[1]}</span></h1>
        <div className="intro-action">
          <button type="button" className="intro-cta" onClick={onEnter}><span>{copy.cta}</span><span className="intro-cta-arrow" aria-hidden="true">→</span></button>
        </div>
        <p className="intro-description">{copy.body}</p>
        <ul className="intro-facts">
          {facts.map(fact => <li key={fact.icon}><FactIcon type={fact.icon} /><span>{fact.text}</span></li>)}
        </ul>
      </main>
      <footer className="intro-footer">
        <Brand />
        <nav>
          <button type="button" onClick={onRules}>{copy.rules}</button>
          <button type="button" onClick={() => setInfo('privacy')}>{copy.privacy}</button>
          <button type="button" onClick={() => setInfo('contact')}>{copy.contact}</button>
        </nav>
      </footer>
    </div>
    {info && <IntroInfoDialog kind={info} locale={locale} onClose={() => setInfo(null)} />}
  </div>;
}
